import React, { useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Autocomplete, TextField } from "@mui/material";
import { ChasablesContext } from "../../../../context/ChasablesContext";
import { ActionTypeEnum } from "@attelas/shared";
import { useAppInsights } from "../../../../services/appInsights";
import { TelemetryEvents } from "../../../../telemetry/eventNames";
import { TelemetryProp, toTelemetryProps } from "../../../../telemetry/telemetryProps";
import { TelemetrySurface } from "../../../../telemetry/surfaces";
import { useUsers } from "../../../../hooks/useUsers";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";

type AssignedToOption = {
  id: string;
  description: string;
};

const ALL_OPTION: AssignedToOption = { id: "all", description: "All" };
const ME_OPTION_ID = "me";

const CHASABLE_ACTION_TYPES = [ActionTypeEnum.Chasable, ActionTypeEnum.NewInvoice, ActionTypeEnum.PromiseToPayBroken];

export function AssignedToMeFilter() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { trackEvent } = useAppInsights();
  const { currentUser } = useCurrentUser();
  const { users } = useUsers();
  const { assignedToUserId, assignedToMeDisabled, onAssignedToUserIdChange, isAssignedToUser, getActionsForUser } =
    useContext(ChasablesContext);

  const options = useMemo<AssignedToOption[]>(() => {
    const meOption: AssignedToOption = {
      id: ME_OPTION_ID,
      description: "Assigned to me",
    };
    const otherUsers = users
      .filter((user) => user.id !== currentUser?.id)
      .map((user) => ({
        id: user.id,
        description: user.fullName,
      }))
      .sort((a, b) => a.description.localeCompare(b.description));

    return [ALL_OPTION, meOption, ...otherUsers];
  }, [users, currentUser?.id]);

  const value = useMemo(() => {
    if (!assignedToUserId) return ALL_OPTION;
    if (assignedToUserId === currentUser?.id) {
      return options.find((option) => option.id === ME_OPTION_ID) ?? ALL_OPTION;
    }
    return options.find((option) => option.id === assignedToUserId) ?? ALL_OPTION;
  }, [assignedToUserId, currentUser?.id, options]);

  const handleChange = (_: unknown, newValue: AssignedToOption | null) => {
    const nextUserId =
      !newValue || newValue.id === ALL_OPTION.id
        ? null
        : newValue.id === ME_OPTION_ID
          ? (currentUser?.id ?? null)
          : newValue.id;

    trackEvent({
      name: TelemetryEvents.receivableFilterUsed,
      properties: toTelemetryProps({
        [TelemetryProp.surface]: TelemetrySurface.receivablesTable,
        [TelemetryProp.filterType]: "assigned_to",
        [TelemetryProp.value]: nextUserId === null ? "all" : nextUserId === currentUser?.id ? "me" : "other_user",
      }),
    });

    onAssignedToUserIdChange(nextUserId);

    if (!nextUserId || !id) return;

    const userChasableActions = getActionsForUser(nextUserId).filter(({ type }) =>
      CHASABLE_ACTION_TYPES.includes(type)
    );
    if (!isAssignedToUser(id, nextUserId) && userChasableActions.length) {
      navigate(`/chasables/suggested/${userChasableActions[0].id}`);
    }
  };

  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      getOptionLabel={(option) => option.description}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      getOptionDisabled={(option) => option.id === ME_OPTION_ID && assignedToMeDisabled}
      onChange={handleChange}
      renderInput={(params) => <TextField {...params} label="Assigned to" placeholder="All" />}
      sx={{ minWidth: 200 }}
    />
  );
}
